import { MAX_NUMBER_OF_POSSIBLE_GAMES_PER_MATCH } from '../../utils/config/config';

export type GameType = {
    home_points: number;
    guest_points: number;
};

// Problem statement: Inside the setup mask, I want to be able to update the score of one game correctly
// Logic:
//   When clicking the edit button, I should be able to update all three games as I like
//   The games need to be checked for sanity (Are the previous games finished or not)
//   Unnecessary games need to be removed (if the game ended after the second game, the third one should not be displayed anywhere)
//   When clicking the save button, the input should be sanity checked and the unnecessary games need to be removed because the match should only show finished games

export const extendGamesToMaximumNumberOfGames = (games: GameType[]) => {
    let result: GameType[] = [];
    for (let i = 0; i < MAX_NUMBER_OF_POSSIBLE_GAMES_PER_MATCH; i++) {
        result.push(games[i] === undefined ? { home_points: 0, guest_points: 0 } : games[i]);
    }
    return result;
};

export const previousGamesAreValid = (games: GameType[]): { result: boolean; message?: string } => {
    if (games.length === 1) {
        return { result: true };
    }
    if (games.length === 3) {
        const gameResults = games.map((game) => isGameFinished(game));
        if (gameResults[0].result && gameResults[1].result) {
            if (gameResults[0].wonBy !== gameResults[1].wonBy) {
                return { result: true };
            } else {
                return { result: false, message: 'First two games won by the same team. Third game not possible!' };
            }
        } else {
            return {
                result: false,
                message: 'First two games are not finished yet.',
            };
        }
    }
    if (isGameFinished(games[0]).result) {
        return { result: true };
    }
    return { result: false, message: 'First game is not finished yet. Second game can not be changed' };
};

export const isGameFinished = (games: GameType): { wonBy?: string; result: boolean } => {
    const homePoints = games.home_points;
    const guestPoints = games.guest_points;
    if ((homePoints >= 21 && homePoints - guestPoints >= 2) || homePoints === 30) {
        return { wonBy: 'home', result: true };
    }
    if ((guestPoints >= 21 && guestPoints - homePoints >= 2) || guestPoints === 30) {
        return { wonBy: 'guest', result: true };
    }
    return { result: false };
};

export const removeNotNeededGames = (games: GameType[]) => {
    // Guard: no input
    if (games.length === 0) {
        throw new Error('Empty list is not allowed');
    }

    // Single game -> nothing to remove
    if (games.length === 1) {
        return games;
    }

    // Helper predicates
    const isStarted = (g: GameType) => g.home_points + g.guest_points > 0;
    const isFinishedGame = (g: GameType) => isGameFinished(g).result;

    // If previous games already valid, keep as is
    const previousValid = previousGamesAreValid(games).result;
    if (previousValid) {
        return games;
    }

    // Two games: if first finished keep both, otherwise only keep first
    if (games.length === 2) {
        return isFinishedGame(games[0]) ? games : games.slice(0, 1);
    }

    // Three games: clearer linear rules instead of nested conditions
    // If first game finished -> keep second; keep third only if it has been started
    if (isFinishedGame(games[0])) {
        return isStarted(games[2]) ? games : games.slice(0, 2);
    }

    // First game not finished -> keep second only if it started; else keep third only if it started
    if (isStarted(games[1])) {
        return games.slice(0, 2);
    }
    if (isStarted(games[2])) {
        // Rare case: second not started but third has points -> keep all three
        return games;
    }

    // default: only keep the first
    return games.slice(0, 1);
};
